import { Badge, Banner, Button, Field, Input, InputArea, Select, Switch } from '@cloudflare/kumo';
import { FormPage, FormSection } from '../components/ui/FormPage';
import { useCallback, useEffect, useState } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';
import { DangerConfirmModal } from '../components/DangerConfirmModal';
import { DangerButton } from '../components/ui/DangerButton';
import { ICONS } from '../components/ui/icons';
import { SkeletonText } from '../components/ui/Skeleton';

interface CaptureLink {
  id: string; name: string; sessionName: string; keyword: string; categoryId: string | null; replyText: string; consentText: string;
  active: boolean; hits: number; createdAt: string; url: string | null; qr: string | null; formUrl?: string;
}
interface Data {
  links: CaptureLink[]; defaultConsent: string; consentedContacts: number;
  sessions: Array<{ name: string; displayName: string | null; provider: string; status: string; phone: string | null }>;
  categories: Array<{ id: string; nome: string }>;
}

const EMPTY = { name: '', sessionName: '', keyword: '', categoryId: '', replyText: 'Oba! 🎉 Você vai receber nossas novidades e ofertas por aqui. Para sair a qualquer momento, responda SAIR.', consentText: '' };
/** O `Select` do Cloudscape não aceita valor vazio — usamos uma sentinela. */
const NONE = '__none__';

/** Contatos › Captação: link wa.me e QR Code com palavra-chave e consentimento (LGPD) */
export function CaptacaoPage() {
  const { can } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [data, setData] = useState<Data | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<CaptureLink | null>(null);
  const canEdit = can('contacts:write');

  const load = useCallback(async () => {
    const res = await fetchApi('/api/captacao');
    if (res.ok) setData(await res.json());
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const res = await fetchApi('/api/captacao', { method: 'POST', body: JSON.stringify({ ...form, categoryId: form.categoryId || undefined, consentText: form.consentText || undefined }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Não foi possível criar');
      toast.success('Captação criada: divulgue o link ou o QR Code');
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível criar');
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id: string, body: Record<string, unknown>) => {
    const res = await fetchApi(`/api/captacao/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
    if (!res.ok) toast.error('Não foi possível salvar'); else load();
  };

  const copy = async (text: string) => { try { await navigator.clipboard.writeText(text); toast.success('Link copiado'); } catch { /* sem permissão */ } };

  if (!data) return <div className="py-8"><SkeletonText lines={4} /></div>;
  const catName = (id: string | null) => data.categories.find((c) => c.id === id)?.nome;

  const sessionOptions = data.sessions.map((s) => ({ value: s.name, label: `${s.displayName || s.name}${s.phone ? ` (+${s.phone})` : ''}` }));
  const categoryOptions = [{ value: NONE, label: 'Nenhuma' }, ...data.categories.map((c) => ({ value: c.id, label: c.nome }))];

  if (open) {
    return (
      <FormPage
        backTo="/captacao"
        backLabel="Captação"
        onBack={() => setOpen(false)}
        title={'Nova captação'}
        description="Capte contatos a partir de uma palavra-chave recebida no WhatsApp."
        submitLabel={'Criar'}
        submitting={busy}
        submitDisabled={!form.keyword.trim() || !form.sessionName}
        onSubmit={save}
        onCancel={() => setOpen(false)}
      >
        <FormSection title="Detalhes">


        <div className="flex flex-col gap-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Nome" description="Onde será divulgada (ex.: Balcão, Instagram)">
              <Input onChange={(e) => setForm({ ...form, name: e.target.value })} value={form.name} />
            </Field>
            <Field label="Palavra-chave" description="O que a pessoa envia (ex.: OFERTAS)">
              <Input onChange={(e) => setForm({ ...form, keyword: e.target.value })} value={form.keyword} />
            </Field>
            <Field label="Número que recebe">
              <Select value={(sessionOptions.find((o) => o.value === form.sessionName) ?? null)?.value ?? null} items={sessionOptions} onValueChange={(value: string | null) => {
                  const v = value;
                  if (!v) return;
                  setForm({ ...form, sessionName: v });
                }} placeholder="Selecione o número" />
            </Field>
            <Field label="Colocar na categoria">
              <Select value={(categoryOptions.find((o) => o.value === (form.categoryId || NONE)) ?? null)?.value ?? null} items={categoryOptions} onValueChange={(value: string | null) => {
                  const v = value;
                  if (!v) return;
                  setForm({ ...form, categoryId: v === NONE ? '' : v });
                }} />
            </Field>
          </div>
          <InputArea label="Resposta automática" onChange={(e) => setForm({ ...form, replyText: e.target.value })} value={form.replyText} rows={3} />
          <InputArea label="Texto do consentimento registrado" description="Fica gravado no contato como prova do opt-in" onChange={(e) => setForm({ ...form, consentText: e.target.value })} value={form.consentText} rows={2} />
        </div>
        
        </FormSection>
      </FormPage>
    );
  }

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Captação com consentimento</h2>
            <div style={{ color: 'var(--text-muted)', maxWidth: 760 }}>
              Divulgue um link ou QR Code (no balcão, no Instagram, na embalagem). Quem enviar a palavra-chave vira contato com o consentimento registrado, como pede a LGPD, e recebe a sua resposta na hora.
            </div>
          </div>
          {canEdit && <Button variant="primary" icon={ICONS.Plus} onClick={() => { setForm({ ...EMPTY, sessionName: data.sessions.find((s) => s.status === 'WORKING')?.name || '', consentText: data.defaultConsent }); setOpen(true); }}>Nova captação</Button>}
        </div>
      </div>

      <div className="page-content space-y-6">
        <div className="tools-stats">
          <div className="tools-stat"><small>Contatos com consentimento</small><strong style={{ color: 'var(--brand-text)' }}>{data.consentedContacts.toLocaleString('pt-BR')}</strong><span className="tools-stat__hint">use "só quem consentiu" na nova campanha</span></div>
          <div className="tools-stat"><small>Adesões pelos links</small><strong>{data.links.reduce((n, l) => n + l.hits, 0).toLocaleString('pt-BR')}</strong></div>
        </div>

        {data.links.length === 0 && <Banner variant="default" description={<>Nenhuma captação ainda. Crie uma palavra-chave (por exemplo, OFERTAS) e divulgue o link.</>} />}

        <div className="capture-grid">
          {data.links.map((l) => (
            <section key={l.id} className="brand-card capture-card">
              <div className="flex items-start justify-between gap-3">
                <div style={{ minWidth: 0 }}>
                  <h4 className="text-base font-semibold">{l.name}</h4>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Palavra <strong>{l.keyword}</strong> · {l.sessionName}{catName(l.categoryId) ? ` · categoria ${catName(l.categoryId)}` : ''}</div>
                </div>
                <Badge variant={l.active ? 'green' : 'neutral'}>{l.active ? `${l.hits} adesões` : 'Pausada'}</Badge>
              </div>
              <div className="capture-body">
                {l.qr ? <img src={l.qr} alt={`QR Code da captação ${l.name}`} /> : <div className="capture-noqr">Conexão sem número conhecido: conecte-a para gerar o link</div>}
                <div style={{ minWidth: 0, flex: 1 }}>
                  {l.url && (
                    <>
                      <code className="capture-url">{l.url}</code>
                      <div className="flex gap-2 flex-wrap" style={{ marginTop: 8 }}>
                        <Button variant="secondary" icon={ICONS.Copy} onClick={() => copy(l.url!)}>Copiar link</Button>
                        {l.qr && <a href={l.qr} download={`qrcode-${l.keyword.toLowerCase()}.png`}><Button variant="secondary" icon={ICONS.Download}>Baixar QR</Button></a>}
                      </div>
                    </>
                  )}
                  {l.formUrl && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Formulário para site e bio (nome, WhatsApp e consentimento):</div>
                      <div className="flex gap-2 flex-wrap" style={{ marginTop: 4 }}>
                        <Button variant="secondary" icon={ICONS.Copy} onClick={() => copy(l.formUrl!)}>Copiar formulário</Button>
                        <a href={l.formUrl} target="_blank" rel="noreferrer"><Button variant="secondary" icon={ICONS.OpenNewTab}>Abrir</Button></a>
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>Resposta: “{l.replyText}”</div>
                </div>
              </div>
              {canEdit && (
                <div className="flex items-center justify-between" style={{ marginTop: 12 }}>
                  <label className="flex items-center gap-2"><Switch onCheckedChange={(checked: boolean) => patch(l.id, { active: checked })} aria-label="Ativa" checked={l.active} /><div style={{ fontSize: 13 }}>Ativa</div></label>
                  <DangerButton variant="dangerOutline" onClick={() => setRemoving(l)}>Excluir</DangerButton>
                </div>
              )}
            </section>
          ))}
        </div>
      </div>



      <DangerConfirmModal open={!!removing} title="Excluir captação" confirmText="Excluir" objectName={removing?.name} onCancel={() => setRemoving(null)}
        onConfirm={async () => { const l = removing; setRemoving(null); if (l) { const r = await fetchApi(`/api/captacao/${l.id}`, { method: 'DELETE' }); if (r.ok) { toast.success('Captação excluída'); load(); } } }}>
        {removing ? `A palavra ${removing.keyword} deixa de registrar contatos. O consentimento de quem já aderiu continua gravado.` : ''}
      </DangerConfirmModal>
    </>
  );
}
